import React from "react";
import { motion } from "framer-motion";
import { Accessibility, Layers, Wrench, Users, Siren, ClipboardCheck, Megaphone, Check, AlertTriangle } from "lucide-react";
import { useLang } from "../../i18n";
import { paraGrades, adaptiveEquipment, sidewalkerBriefing, paraEmergency, paraPreRide, paraAwareness } from "../../data/safety";

const Panel = ({ icon: Icon, title, children, note }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    className="grid-panel rounded-lg overflow-hidden"
  >
    <div className="flex items-center gap-2.5 px-4 py-3 border-b border-bronze/15 bg-tiefsee-2/40">
      <Icon size={18} className="text-sonne" />
      <span className="font-mono text-sm tracking-wide">{title}</span>
    </div>
    <div className="p-4">
      {children}
      {note && <p className="mt-3 text-xs text-wachs-dim/80 italic">{note}</p>}
    </div>
  </motion.div>
);

const List = ({ items }) => {
  const { lang } = useLang();
  return (
    <ul className="space-y-2">
      {items.map((it) => (
        <li key={lang === "de" ? it.de : it.en} className="flex items-start gap-2 text-sm text-wachs-dim">
          <Check size={15} className="text-ready shrink-0 mt-0.5" /> <span>{lang === "de" ? it.de : it.en}</span>
        </li>
      ))}
    </ul>
  );
};

export default function ParaSafety() {
  const { t, lang } = useLang();
  return (
    <div>
      <div className="flex items-center gap-2 instrument-label text-sonne">
        <Accessibility size={16} /> FEI Para Dressage 2026 · RDA
      </div>
      <p className="mt-3 text-sm text-wachs-dim leading-relaxed max-w-2xl">{t.safety.para.intro}</p>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {/* Classification */}
        <Panel icon={Layers} title={t.safety.para.gradesTitle} note={t.safety.para.gradesNote}>
          <ul className="space-y-2">
            {paraGrades.map((g) => (
              <li key={g.grade} className="flex items-start gap-3 text-sm text-wachs-dim" data-testid={`para-grade-${g.grade}`}>
                <span className="font-mono text-sonne w-9 shrink-0 text-center border border-sonne/30 rounded">{g.grade}</span>
                <span>{lang === "de" ? g.de : g.en}</span>
              </li>
            ))}
          </ul>
        </Panel>

        {/* Adaptive equipment */}
        <Panel icon={Wrench} title={t.safety.para.equipmentTitle} note={t.safety.para.equipmentNote}>
          <List items={adaptiveEquipment} />
        </Panel>

        {/* Sidewalker briefing */}
        <Panel icon={Users} title={t.safety.para.sidewalkerTitle}>
          <List items={sidewalkerBriefing} />
        </Panel>

        {/* Emergency protocol */}
        <Panel icon={Siren} title={t.safety.para.emergencyTitle}>
          <ul className="space-y-2">
            {paraEmergency.map((it) => (
              <li key={lang === "de" ? it.de : it.en} className="flex items-start gap-2 text-sm text-wachs-dim">
                <AlertTriangle size={14} className="text-sonne shrink-0 mt-0.5" /> <span>{lang === "de" ? it.de : it.en}</span>
              </li>
            ))}
          </ul>
        </Panel>

        {/* Para pre-ride add-on */}
        <Panel icon={ClipboardCheck} title={t.safety.para.prerideTitle}>
          <List items={paraPreRide} />
        </Panel>

        {/* Awareness & education */}
        <Panel icon={Megaphone} title={t.safety.para.awarenessTitle}>
          <List items={paraAwareness} />
        </Panel>
      </div>
    </div>
  );
}
